import React, { useState, useRef, useEffect, useMemo } from "react";
import ReactMapGL, { Marker, FlyToInterpolator } from "react-map-gl";
import useSupercluster from "use-supercluster";
import MerdeIcon from "./MerdeIcon";

/* ======================
   STATUS PRIORITY (cluster)
   Basert på 2. tegn: G < Y < R
   ====================== */

const getWorstStatus = (features) => {
  let worst = null;

  for (const f of features) {
    const status = f?.properties?.status;
    if (!status || typeof status !== "string" || status.length < 2) continue;

    const sev = status[1].toUpperCase();

    if (sev === "R") return status;
    if (sev === "Y") worst = status;
    if (sev === "G" && !worst) worst = status;
  }

  return worst;
};

const MapboxGLMerdeMap = ({ myContent }) => {
  const [viewport, setViewport] = useState({
    latitude: 59.42249,
    longitude: 10.456476,
    zoom: 3,
  });

  const mapRef = useRef(null);

  /* ======================
     POINTS
     ====================== */

  const points = useMemo(
    () =>
      myContent.map((merde) => ({
        type: "Feature",
        properties: {
          cluster: false,
          merdeId: merde.id,
          merdeNick: merde.nick,
          status: merde.status ?? null,
        },
        geometry: merde.geometry,
      })),
    [myContent]
  );

  /* ======================
     BOUNDS
     ====================== */

  let bounds = null;
  try {
    const map = mapRef.current?.getMap?.();
    if (map?.getBounds) {
      bounds = map.getBounds().toArray().flat();
    }
  } catch {}

  const { clusters, supercluster } = useSupercluster({
    points,
    bounds,
    zoom: Math.round(viewport.zoom),
    options: { radius: 75, maxZoom: 16 },
  });

  /* ======================
     WINDOW RESIZE (etter load)
     ====================== */

  useEffect(() => {
    const onResize = () => {
      try {
        mapRef.current?.getMap?.()?.resize();
      } catch {}
    };

    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  /* ======================
     RENDER
     ====================== */

  return (
    <div className="map-wrapper" style={{ width: "100%", height: "100%" }}>
      <ReactMapGL
        {...viewport}
        width="100%"
        height="100%"
        maxZoom={16}
        mapboxApiAccessToken="pk.eyJ1Ijoib2xlcnUiLCJhIjoiY2tobmcwb2pyMHA0cDJwcXFrZnJwNDZnaSJ9.bRAY-DErXfYbb_6yLxy9ng"
        mapStyle="mapbox://styles/mapbox/satellite-v9"
        ref={mapRef}
        onLoad={() => {
          // 🔴 KRITISK: første resize etter load
          try {
            mapRef.current?.getMap?.()?.resize();
          } catch {}
        }}
        onViewportChange={(vp) =>
          setViewport((prev) => ({ ...prev, ...vp }))
        }
      >
        {clusters.map((cluster) => {
          const [longitude, latitude] = cluster.geometry.coordinates;
          const { cluster: isCluster, point_count: pointCount } =
            cluster.properties;

          const handleClick = () => {
            const zoom = isCluster
              ? Math.min(
                  supercluster.getClusterExpansionZoom(cluster.id),
                  20
                )
              : 16;

            setViewport((prev) => ({
              ...prev,
              latitude,
              longitude,
              zoom,
              transitionInterpolator: new FlyToInterpolator({ speed: 2 }),
              transitionDuration: "auto",
            }));
          };

          if (isCluster) {
            const leaves = supercluster.getLeaves(cluster.id);
            const worstStatus = getWorstStatus(leaves);

            return (
              <Marker
                key={`cluster-${cluster.id}`}
                latitude={latitude}
                longitude={longitude}
                offsetLeft={-45}
                offsetTop={-45}
              >
                <button className="merd-marker" onClick={handleClick}>
                  <MerdeIcon status={worstStatus} size={72} />
                </button>

                <div className="merd-text" onClick={handleClick}>
                  {pointCount}
                </div>
              </Marker>
            );
          }

          return (
            <Marker
              key={`merde-${cluster.properties.merdeId}`}
              latitude={latitude}
              longitude={longitude}
              offsetLeft={-45}
              offsetTop={-45}
            >
              <button className="merd-marker" onClick={handleClick}>
                <MerdeIcon
                  status={cluster.properties.status}
                  size={72}
                />
              </button>

              <div className="merd-text" onClick={handleClick}>
                {cluster.properties.merdeNick}
              </div>
            </Marker>
          );
        })}
      </ReactMapGL>
    </div>
  );
};

export default MapboxGLMerdeMap;
